import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Sparkles, ArrowDown } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Wrapper that force-presents the latest Service Agreement to any authenticated
 * user whose `service_agreement_version` doesn't match the currently published
 * version.
 *
 * UX rules:
 *  - Modal is non-dismissable (no Esc, no outside-click, no X).
 *  - The Accept button stays disabled until the user has BOTH scrolled to the
 *    bottom of the agreement AND ticked the checkbox.
 *  - If the agreement is short enough to not require scrolling, the
 *    "scrolled" gate is auto-satisfied on render.
 */
export default function ServiceAgreementGate({ children }) {
  const { token, user, refreshUser, logout } = useAuth();
  const { language } = useLanguage();
  const es = language === 'es';
  const [status, setStatus] = useState(null);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const scrollRef = useRef(null);
  const openedAtRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    if (!token || !user) { setStatus(null); return; }
    try {
      const r = await axios.get(`${API}/service-agreement/me`, { headers: { Authorization: `Bearer ${token}` } });
      setStatus(r.data);
    } catch {
      setStatus(null);
    }
  }, [token, user]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Whenever a new version is presented, reset the gates and the read-timer.
  useEffect(() => {
    setChecked(false);
    setScrolledToEnd(false);
    if (status?.needs_acceptance) {
      openedAtRef.current = Date.now();
    }
  }, [status?.current_version, status?.needs_acceptance, language]);

  // On open / language switch: if content fits without scrolling, auto-satisfy.
  useEffect(() => {
    if (!status?.needs_acceptance) return;
    // Wait for layout
    const id = requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (!el) return;
      if (el.scrollHeight <= el.clientHeight + 4) {
        setScrolledToEnd(true);
      }
    });
    return () => cancelAnimationFrame(id);
  }, [status?.needs_acceptance, status?.current_version, language]);

  const handleScroll = (e) => {
    const el = e.currentTarget;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining < 16) setScrolledToEnd(true);
  };

  if (!status?.needs_acceptance) return children;

  const content = es ? status.content_es : status.content_en;

  const accept = async () => {
    if (!scrolledToEnd) {
      toast.error(es ? 'Lee el acuerdo hasta el final' : 'Read the agreement to the end first');
      return;
    }
    if (!checked) {
      toast.error(es ? 'Marca la casilla para continuar' : 'Tick the box to continue');
      return;
    }
    setSubmitting(true);
    try {
      const readDurationMs = openedAtRef.current ? Math.max(0, Date.now() - openedAtRef.current) : null;
      await axios.post(
        `${API}/service-agreement/accept`,
        { version: status.current_version, source: 'force_modal', read_duration_ms: readDurationMs },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(es ? 'Acuerdo aceptado' : 'Agreement accepted');
      await refreshUser();
      setStatus({ ...status, needs_acceptance: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || (es ? 'Error al aceptar' : 'Acceptance failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const acceptDisabled = !scrolledToEnd || !checked || submitting;

  return (
    <>
      {children}
      <Dialog open={true}>
        <DialogContent
          className="sm:max-w-2xl w-[calc(100vw-32px)] max-h-[92vh] flex flex-col p-5 sm:p-6 gap-3 [&>button.absolute]:hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          data-testid="service-agreement-modal"
        >
          <DialogHeader className="shrink-0 space-y-2">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-[10px] font-bold uppercase tracking-wider self-start">
              <Sparkles className="w-3 h-3" />
              {es ? `Versión ${status.current_version}` : `Version ${status.current_version}`}
            </div>
            <DialogTitle className="font-heading text-xl sm:text-2xl text-[#0B192C]" data-testid="service-agreement-modal-title">
              {es ? 'Acuerdo de Servicio actualizado' : 'Service Agreement updated'}
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              {es
                ? 'Para continuar usando WakaCasa, revisa y acepta la versión más reciente.'
                : 'To keep using WakaCasa, please review and accept the latest version.'}
            </DialogDescription>
          </DialogHeader>

          {/* Scrollable agreement body — flex-1 makes it the ONLY part that shrinks */}
          <div className="relative flex-1 min-h-[120px]">
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="absolute inset-0 overflow-y-auto border border-slate-200 rounded-lg p-4 bg-slate-50"
              data-testid="service-agreement-scroll"
            >
              <article
                className="prose prose-sm prose-slate max-w-none prose-headings:font-heading prose-headings:text-[#0B192C] prose-h1:text-xl prose-h2:text-base prose-h2:mt-5 prose-h2:mb-2 prose-p:text-slate-600 prose-p:leading-relaxed prose-strong:text-[#0B192C]"
                data-testid="service-agreement-modal-content"
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{content || ''}</ReactMarkdown>
              </article>
            </div>
            {!scrolledToEnd && (
              <div
                className="pointer-events-none absolute bottom-2 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#0B192C] text-[#D4AF37] text-[11px] shadow-md animate-pulse"
                data-testid="service-agreement-scroll-hint"
              >
                <ArrowDown className="w-3 h-3" />
                {es ? 'Desplázate hasta el final' : 'Scroll to the end'}
              </div>
            )}
          </div>

          {/* Checkbox row — always visible, doesn't shrink */}
          <div className="shrink-0 flex items-start gap-2 pt-1" data-testid="service-agreement-checkbox-row">
            <Checkbox
              id="sa-accept-cb"
              checked={checked}
              onCheckedChange={(v) => setChecked(!!v)}
              disabled={!scrolledToEnd}
              className="mt-0.5 border-slate-400 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37] disabled:opacity-60"
              data-testid="service-agreement-checkbox"
            />
            <label
              htmlFor="sa-accept-cb"
              className={`text-sm leading-snug flex-1 ${scrolledToEnd ? 'text-slate-800 cursor-pointer' : 'text-slate-500 cursor-not-allowed'}`}
            >
              {es
                ? `He leído y acepto la versión ${status.current_version} del Acuerdo de Servicio de WakaCasa.`
                : `I have read and accept version ${status.current_version} of the WakaCasa Service Agreement.`}
            </label>
          </div>

          <DialogFooter className="shrink-0 gap-2 sm:gap-0 pt-1">
            <Button
              type="button"
              variant="ghost"
              onClick={() => { logout(); }}
              className="text-slate-500"
              data-testid="service-agreement-logout-btn"
            >
              {es ? 'Cerrar sesión' : 'Sign out'}
            </Button>
            <Button
              type="button"
              onClick={accept}
              disabled={acceptDisabled}
              className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] font-semibold gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="service-agreement-accept-btn"
            >
              {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {es ? 'Aceptar y continuar' : 'Accept and continue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
